import React, { useState } from "react";
import { playClickSound, playCloseSound, playTabSound } from "../../Sounds";
import { getPrestigeBonus } from "../../utils";
import imageData from "../../data/imageData";

// ─── CONFIG ───────────────────────────────────────────────────
const PRESTIGE_LEVELS = [
  { level:1,  emoji:"🌟", title:"Novice",        colour:"#00e5ff", perk:"+5% luck · +10% coins · 50 start coins"                },
  { level:2,  emoji:"💫", title:"Seasoned",       colour:"#4caf50", perk:"+10% luck · +20% coins · 100 start coins · 2× dupe coins" },
  { level:3,  emoji:"🔥", title:"Veteran",        colour:"#ff9800", perk:"+15% luck · +30% coins · 150 start coins · free Lucky Charm" },
  { level:4,  emoji:"💎", title:"Elite",          colour:"#2196f3", perk:"+20% luck · +40% coins · 200 start coins · faster pity"    },
  { level:5,  emoji:"👑", title:"Legend",         colour:"#e91e63", perk:"+25% luck · +50% coins · 250 start coins · guaranteed Rare first roll" },
  { level:6,  emoji:"🌈", title:"Mythmaker",      colour:"#9c27b0", perk:"+30% luck · +60% coins · 300 start coins · Hot Streak on start" },
  { level:7,  emoji:"⚡", title:"Transcendent",   colour:"#ffd700", perk:"+35% luck · +70% coins · 350 start coins · Jackpot ×2 chance" },
  { level:8,  emoji:"🌌", title:"Cosmic",         colour:"#ff2d78", perk:"+40% luck · +80% coins · 400 start coins · Epic Guarantee on start" },
  { level:9,  emoji:"♾️", title:"Eternal",        colour:"#00e5ff", perk:"+45% luck · +90% coins · 450 start coins · Mythic chance ×2" },
  { level:10, emoji:"🐹", title:"Hamster God",    colour:"#ffd700", perk:"+50% luck · +100% coins · 500 start coins · ULTRA chance ×2" },
];


const TOTAL_HAMSTERS = imageData.length;
const MAX_PRESTIGE   = PRESTIGE_LEVELS.length;

function getRequirements(nextLevel) {
  return {
    // cap hamster requirement at the actual number of hamsters in the game
    hamsters: Math.min(10 + nextLevel * 4, TOTAL_HAMSTERS),
    rolls:    100 + nextLevel * 40,
  };
}

// ─── SUB-COMPONENTS ───────────────────────────────────────────
function ProgressBar({ value, max, colour }) {
  const pct = Math.min(value / max, 1) * 100;
  const met = value >= max;
  return (
    <div className="prestige-req-item">
      <div className="prestige-req-header">
        <span style={{ color: met ? "#4caf50" : "var(--text-muted)" }}>
          {met ? "✓ " : ""}{value} / {max}
        </span>
      </div>
      <div className="prestige-req-bar">
        <div className="prestige-req-fill" style={{
          width: `${pct}%`,
          background: met ? "#4caf50" : colour,
          boxShadow: met ? "0 0 8px #4caf5088" : `0 0 8px ${colour}66`,
          transition: "width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
        }} />
      </div>
    </div>
  );
}

function StarRow({ count, colour }) {
  return (
    <div className="prestige-star-row">
      {Array.from({ length: Math.min(count, 10) }).map((_, i) => (
        <span key={i} className="prestige-star" style={{
          animationDelay: `${i * 0.08}s`,
          color: colour || "#ffd700",
          textShadow: `0 0 12px ${colour || "#ffd700"}`,
        }}>⭐</span>
      ))}
      {count === 0 && <span style={{ color: "var(--text-muted)", fontSize: ".85rem" }}>No prestige yet</span>}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────
export default function Prestige({ prestige, inventory, rollCount, onConfirm, onClose }) {
  const [tab,       setTab]       = useState("next");
  const [confirmed, setConfirmed] = useState(false);

  const isMaxPrestige = prestige >= MAX_PRESTIGE;
  const nextLevel     = prestige + 1;
  const req           = getRequirements(nextLevel);
  const meetsH        = inventory.length >= req.hamsters;
  const meetsR        = rollCount >= req.rolls;
  const canPrestige   = !isMaxPrestige && meetsH && meetsR;

  const currentBonus = getPrestigeBonus(prestige);
  const nextBonus    = getPrestigeBonus(nextLevel);
  const currentLevel = PRESTIGE_LEVELS[Math.min(prestige - 1, PRESTIGE_LEVELS.length - 1)];
  const nextLevelDef = PRESTIGE_LEVELS[Math.min(nextLevel - 1, PRESTIGE_LEVELS.length - 1)];
  const colour       = isMaxPrestige ? (currentLevel?.colour || "#ffd700") : (nextLevelDef?.colour || "#ffd700");

  return (
    <div className="modal-overlay" onClick={() => { playCloseSound(); onClose(); }}>
      <div className="modal-box prestige-box" onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="prestige-hero" style={{ borderColor: colour + "44" }}>
          <div className="prestige-hero-emoji">
            {isMaxPrestige ? "🐹" : (nextLevelDef?.emoji || "⭐")}
          </div>
          <h2 style={{ margin: "6px 0 2px" }}>Prestige</h2>
          {prestige > 0 ? (
            <div className="prestige-hero-sub" style={{ color: currentLevel?.colour || "#ffd700" }}>
              {currentLevel?.emoji} {currentLevel?.title} · Level {prestige}
            </div>
          ) : (
            <div className="prestige-hero-sub">Begin your prestige journey</div>
          )}
          <StarRow count={prestige} colour={currentLevel?.colour} />
        </div>

        {/* ── Tabs (hidden when max prestige — only show roadmap) ── */}
        {!isMaxPrestige && (
          <div className="prestige-tabs">
            {[["next","Next Prestige"], ["roadmap","Roadmap"]].map(([id, label]) => (
              <button key={id}
                className={`prestige-tab${tab === id ? " active" : ""}`}
                style={tab === id ? { background: colour, color: "#0a0612" } : {}}
                onClick={() => { playTabSound(); setTab(id); setConfirmed(false); }}
              >{label}</button>
            ))}
          </div>
        )}

        {/* ── MAX PRESTIGE REACHED ── */}
        {isMaxPrestige && (
          <>
            <div className="prestige-maxed-banner">
              <div className="prestige-maxed-emoji">🏆</div>
              <div className="prestige-maxed-title">Maximum Prestige Reached!</div>
              <div className="prestige-maxed-sub">
                You've achieved <strong style={{ color: currentLevel?.colour }}>Hamster God</strong> status.<br />
                There's nothing left to unlock — you are the ultimate hamster collector.
              </div>
            </div>

            <div className="prestige-active-bonuses">
              <div className="prestige-section-title">👑 Your permanent bonuses</div>
              <div className="prestige-bonus-chips">
                <span className="prestige-bonus-chip luck">+{Math.round((currentBonus.luckMult-1)*100)}% Luck</span>
                <span className="prestige-bonus-chip coin">+{Math.round((currentBonus.coinMult-1)*100)}% Coins</span>
                <span className="prestige-bonus-chip start">🪙 {currentBonus.startCoins} Start coins</span>
              </div>
            </div>

            <div className="prestige-roadmap" style={{ marginTop: 16 }}>
              {PRESTIGE_LEVELS.map(lvl => (
                <div key={lvl.level} className="prestige-road-item done"
                  style={{ borderColor: lvl.colour + "55" }}>
                  <div className="prestige-road-icon" style={{ color: lvl.colour }}>⭐</div>
                  <div className="prestige-road-body">
                    <div className="prestige-road-title" style={{ color: lvl.colour }}>
                      {lvl.emoji} Level {lvl.level} — {lvl.title}
                      <span className="prestige-road-done-badge">✓</span>
                    </div>
                    <div className="prestige-road-perk">{lvl.perk}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── NEXT PRESTIGE TAB ── */}
        {!isMaxPrestige && tab === "next" && (
          <>
            {prestige > 0 && (
              <div className="prestige-active-bonuses">
                <div className="prestige-section-title">✨ Active bonuses</div>
                <div className="prestige-bonus-chips">
                  <span className="prestige-bonus-chip luck">+{Math.round((currentBonus.luckMult-1)*100)}% Luck</span>
                  <span className="prestige-bonus-chip coin">+{Math.round((currentBonus.coinMult-1)*100)}% Coins</span>
                  {currentBonus.startCoins > 0 && <span className="prestige-bonus-chip start">🪙 {currentBonus.startCoins} Start</span>}
                </div>
              </div>
            )}

            <div className="prestige-reward-card" style={{ borderColor: colour + "55", background: colour + "08" }}>
              <div className="prestige-reward-header">
                <span className="prestige-reward-badge" style={{ background: colour, color: "#0a0612" }}>
                  PRESTIGE {nextLevel}
                </span>
                <span className="prestige-reward-title">{nextLevelDef?.emoji} {nextLevelDef?.title}</span>
              </div>
              <div className="prestige-reward-perks">
                <div className="prestige-perk-row">
                  <span className="prestige-perk-icon">🍀</span>
                  <span>+{Math.round((nextBonus.luckMult-1)*100)}% total luck <span className="prestige-perk-delta">(+{Math.round((nextBonus.luckMult - currentBonus.luckMult)*100)}%)</span></span>
                </div>
                <div className="prestige-perk-row">
                  <span className="prestige-perk-icon">🪙</span>
                  <span>+{Math.round((nextBonus.coinMult-1)*100)}% coin bonus <span className="prestige-perk-delta">(+{Math.round((nextBonus.coinMult - currentBonus.coinMult)*100)}%)</span></span>
                </div>
                <div className="prestige-perk-row">
                  <span className="prestige-perk-icon">🎁</span>
                  <span>🪙 {nextBonus.startCoins} coins at run start</span>
                </div>
                {nextLevelDef?.perk && (
                  <div className="prestige-perk-row prestige-perk-special">
                    <span className="prestige-perk-icon">⚡</span>
                    <span>{nextLevelDef.perk.split("·").slice(3).join("·").trim() || "Special bonus unlocked"}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="prestige-loss-card">
              <div className="prestige-section-title" style={{ color:"#ff6666" }}>❌ You will lose</div>
              <div className="prestige-loss-list">
                <span>🐹 Your {inventory.length} hamster{inventory.length !== 1 ? "s" : ""}</span>
                <span>🪙 All coins &amp; active boosts</span>
                <span>🎲 Roll count resets to 0</span>
              </div>
              <div className="prestige-keep-list">
                <div className="prestige-section-title" style={{ color:"#4caf50", marginTop:10 }}>✅ You will keep</div>
                <span>🏆 Achievements &amp; daily streak</span>
                <span>📊 Total coins earned (lifetime)</span>
                <span>⭐ All prestige bonuses earned so far</span>
              </div>
            </div>

            <div className="prestige-reqs">
              <div className="prestige-section-title">📋 Requirements for Prestige {nextLevel}</div>
              <div className="prestige-req-row">
                <span className="prestige-req-label">🐹 Hamsters</span>
                <ProgressBar value={inventory.length} max={req.hamsters} colour={colour} />
              </div>
              <div className="prestige-req-row">
                <span className="prestige-req-label">🎲 Rolls</span>
                <ProgressBar value={rollCount} max={req.rolls} colour={colour} />
              </div>
            </div>

            {!confirmed ? (
              <button
                className="prestige-action-btn"
                style={canPrestige ? { background:`linear-gradient(135deg,${colour},${colour}aa)`, color:"#0a0612" } : {}}
                onClick={() => { if (canPrestige) { playClickSound(); setConfirmed(true); } }}
                disabled={!canPrestige}
              >
                {canPrestige
                  ? `⭐ Prestige to Level ${nextLevel}`
                  : `Need ${Math.max(0,req.hamsters-inventory.length)} more hamsters · ${Math.max(0,req.rolls-rollCount)} more rolls`}
              </button>
            ) : (
              <div className="prestige-confirm">
                <p>⚠️ This will reset your collection and coins. Sure?</p>
                <div className="prestige-confirm-row">
                  <button className="prestige-btn-cancel" onClick={() => { playCloseSound(); setConfirmed(false); }}>Cancel</button>
                  <button className="prestige-btn-go" style={{ background:`linear-gradient(135deg,${colour},#ff2d78)` }}
                    onClick={() => { playClickSound(); onConfirm(); }}>
                    Yes, Prestige!
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── ROADMAP TAB (only when not max prestige) ── */}
        {!isMaxPrestige && tab === "roadmap" && (
          <div className="prestige-roadmap">
            {PRESTIGE_LEVELS.map(lvl => {
              const done    = prestige >= lvl.level;
              const current = prestige + 1 === lvl.level;
              const locked  = prestige + 1 < lvl.level;
              const rq      = getRequirements(lvl.level);
              return (
                <div key={lvl.level} className={`prestige-road-item${done?" done":""}${current?" current":""}`}
                  style={{ borderColor: current ? lvl.colour : done ? lvl.colour + "55" : "transparent" }}>
                  <div className="prestige-road-icon" style={{ color: done ? lvl.colour : current ? lvl.colour : "var(--text-muted)" }}>
                    {done ? "⭐" : current ? lvl.emoji : locked ? "🔒" : lvl.emoji}
                  </div>
                  <div className="prestige-road-body">
                    <div className="prestige-road-title" style={{ color: done || current ? lvl.colour : "var(--text-main)" }}>
                      {lvl.emoji} Level {lvl.level} — {lvl.title}
                      {done && <span className="prestige-road-done-badge">✓</span>}
                    </div>
                    <div className="prestige-road-perk">{lvl.perk}</div>
                    {!done && <div className="prestige-road-req">Requires {rq.hamsters} hamsters · {rq.rolls} rolls</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <button className="back-button" onClick={() => { playCloseSound(); onClose(); }} style={{ marginTop:16, width:"100%" }}>
          Close
        </button>
      </div>
    </div>
  );
}